import { useContext, useEffect, useState } from "react";
import { AppContext, UserContext, VersionContext } from "../../Context";
import "./EFSManager.css";
import EFSLineItem from "./EFSLineItem";
import {
    downloadEFSObjectsAsTxt,
    EFSObject,
    generateStringFromEFSObject,
    getAllObjectsWithEFS,
    getEfsOptions,
    getExpiredEFS,
    getPropsedValuesFromFeature,
    sendEFSCancelMessage,
    updateNauticalObjectsWithEfsData,
    uploadEFSMessageToServer,
} from "../../utils/efsUtils";
import { Tab, TabList, TabPanel, Tabs } from "react-tabs";
import NfsButton from "../genericComponents/NfsButton/NfsButton";
import { toast } from "react-toastify";
import TextArea from "../genericComponents/TextArea/TextArea";
import { formatDate } from "../../utils/helpers";
import { GetCodedValueAndAlias } from "../../utils/arcgisUtils";
import Checkbox from "../genericComponents/Checkbox/Checkbox";
import RadioButtonGroup from "../genericComponents/RadioButton/RadioButtonGroup";
import { useDispatch, useSelector } from "react-redux";
import { triggerAlertsRefresh } from "../../store/triggersSlice";
import { RightPanelTabs } from "../../constants/Panels";
import { setRightPanelActiveTab } from "../../store/appSlice";

import CloseIcon from "../../assets/close.svg?react";
import RefreshIcon from "../../assets/versionControl/refresh.svg?react";
import { StoreState } from "../../store/rootReducer";
import { isDefaultVersion } from "../../utils/versioningUtils";
import type Graphic from "@arcgis/core/Graphic";

type EfsManagerStatus = "selection" | "expanded" | "preparation";
enum EfsListMode {
    Active = 0,
    Expired = 1,
    All = 2,
}

const EFSManager = () => {
    const appContext = useContext(AppContext);
    const efsSelectedFeatures = appContext?.efsSelectedFeatures;

    const dispatch = useDispatch();

    const [efsObjects, setEfsObjects] = useState<EFSObject[]>([]);
    const [efsTemplates, setEfsTemplates] = useState<Graphic[]>([]);
    const [allEfs, setAllEfs] = useState<{ [layerName: string]: Graphic[] }>({});
    const [expiredEfs, setExpiredEfs] = useState<{ [layerName: string]: Graphic[] }>({});
    const [isSendingEfs, setIsSendingEfs] = useState<boolean>(false);
    const [efsObjectsMarkedForCompletion, setEfsObjectsMarkedForCompletion] = useState<
        { nfsObject: Graphic; efsMessageToSend: string }[]
    >([]);
    const [efsListMode, setEfsListMode] = useState<EfsListMode>(EfsListMode.Active);

    const [efsManagerStatus, setEfsManagerStatus] = useState<EfsManagerStatus>("selection");

    const versionContext = useContext(VersionContext);
    const versionServiceRef = versionContext?.versionServiceRef.value;
    const activeVersion = useSelector((state: StoreState) => state.version.versionInfo);

    const userContext = useContext(UserContext);
    const user = userContext?.user;

    const checkIfDefaultVersion = () => {
        if (activeVersion && versionServiceRef?.current) {
            return isDefaultVersion(activeVersion, versionServiceRef?.current);
        }
        return false;
    };

    const handleSendEfs = async () => {
        for (const efsObject of efsObjects) {
            if (efsObject.efsMessageToSend === "" || efsObject.efsMessageToSend === undefined) {
                toast.error(
                    `EFS-melding mangler for sjømerkenr ${efsObject.nfsObject.attributes.sjomerke_nr}. Vennligst fyll ut før sending.`
                );
                return;
            }
        }
        setIsSendingEfs(true);
        try {
            if (!user?.value?.fullName || !user?.value?.email) {
                toast.error("Brukerinformasjon ikke tilgjengelig. Vennligst logg inn på nytt.");
                setIsSendingEfs(false);
                return;
            }
            await uploadEFSMessageToServer(efsObjects, user.value?.email);
            // update objects with new EFS messages
            await updateNauticalObjectsWithEfsData(efsObjects, user.value?.fullName);
            // clear the efsObjects after sending
            setEfsObjects([]);
            setEfsManagerStatus("selection");
            dispatch(triggerAlertsRefresh());
            efsSelectedFeatures?.clear();
            refreshEfsObjects();
        } catch (error) {
            console.error("Error sending EFS messages:", error);
            toast.error("Noe gikk galt under sending av EFS-meldinger. Vennligst prøv igjen.");
        }
        setIsSendingEfs(false);
    };

    const handleSendEfsMarkedForCompletion = async () => {
        setIsSendingEfs(true);
        try {
            await sendEFSCancelMessage(efsObjectsMarkedForCompletion, user?.value?.email ?? "Ukjent avsender");
            refreshEfsObjects();
            // Clear the marked for completion objects after sending
            setEfsObjectsMarkedForCompletion([]);
            dispatch(triggerAlertsRefresh());
        } catch (error) {
            console.error("Error sending EFS completion messages:", error);
            toast.error("Noe gikk galt under sending av EFS-avslutninger. Vennligst prøv igjen.");
        }
        setIsSendingEfs(false);
    };
    const updateEfsObject = (newEfsObject: EFSObject) => {
        setEfsObjects((prevEfsObjects) => {
            return prevEfsObjects.map((efsObject) => {
                if (efsObject.nfsObject.attributes.objectid === newEfsObject.nfsObject.attributes.objectid) {
                    return newEfsObject;
                }
                return efsObject;
            });
        });
    };

    const setEfsMessageStringForObject = (efsObject: EFSObject, overwrite: boolean) => {
        if (overwrite || efsObject.efsMessageToSend === "" || efsObject.efsMessageToSend === undefined) {
            // If overwrite is true, set the efsMessage directly
            const newMessage = generateStringFromEFSObject(efsObject);
            updateEfsObject({ ...efsObject, efsMessageToSend: newMessage });
        }
    };

    const setEfsMessageStringForObjects = (overwrite: boolean) => {
        const copiedEfsObjects = [...efsObjects];
        copiedEfsObjects.forEach((efsObject) => {
            if (overwrite || efsObject.efsMessageToSend === "" || efsObject.efsMessageToSend === undefined) {
                const newMessage = generateStringFromEFSObject(efsObject);
                efsObject.efsMessageToSend = newMessage;
            }
        });
        setEfsObjects(copiedEfsObjects);
    };

    const isObjectMarkedForCompletion = (efsObject: Graphic) => {
        return efsObjectsMarkedForCompletion.some(
            (obj) => obj.nfsObject.attributes.objectid === efsObject.attributes.objectid
        );
    };

    const handleCompletionMessageChange = (efsObject: Graphic, newMessage: string) => {
        setEfsObjectsMarkedForCompletion((prev) => {
            const existingObject = prev.find(
                (obj) => obj.nfsObject.attributes.objectid === efsObject.attributes.objectid
            );
            if (existingObject) {
                return prev.map((obj) => {
                    if (obj.nfsObject.attributes.objectid === efsObject.attributes.objectid) {
                        return { ...obj, efsMessageToSend: newMessage };
                    }
                    return obj;
                });
            } else {
                return [...prev, { nfsObject: efsObject, efsMessageToSend: newMessage }];
            }
        });
    };

    const refreshEfsObjects = () => {
        getAllObjectsWithEFS().then((allEfs) => {
            setAllEfs(allEfs);
        });
        getExpiredEFS().then((expiredEfs) => {
            setExpiredEfs(expiredEfs);
        });
    };

    const getExpiredCount = () => {
        return Object.values(expiredEfs).reduce((count, efsObjects) => {
            return count + efsObjects.length;
        }, 0);
    };

    const closePanel = () => {
        setEfsObjects([]);
        efsSelectedFeatures!.value = [];
        dispatch(setRightPanelActiveTab(RightPanelTabs.UNDEFINED));
    };

    const getEfsTypeFromMessage = (message: string | null) => {
        if (!message) return "Ukjent";
        const messageType = message.split("-melding - ")[0];
        if (messageType.length === 1) {
            return messageType;
        } else {
            return "Ukjent";
        }
    };

    const getCleanEfsMessage = (message: string | null) => {
        if (!message) return null;
        const parts = message.split("-melding - ");
        return parts.length > 1 ? parts[1] : message;
    };

    useEffect(() => {
        if (efsSelectedFeatures?.value) {
            let efsObjectsCopy = [...efsObjects];
            // filter out efsobjects that are not in the selected features
            efsObjectsCopy = efsObjectsCopy.filter((efsObject) =>
                efsSelectedFeatures.value.some(
                    (feature) => feature.attributes.objectid === efsObject.nfsObject.attributes.objectid
                )
            );

            // add new efs objects for the selected features that are not already in the efsObjects
            efsSelectedFeatures.value.forEach((feature) => {
                const existingEfsObject = efsObjectsCopy.find(
                    (efsObject) => efsObject.nfsObject.attributes.objectid === feature.attributes.objectid
                );
                if (!existingEfsObject) {
                    const propsedValues = getPropsedValuesFromFeature(feature, efsTemplates);
                    const newEfsObject: EFSObject = {
                        nfsObject: feature,
                        efsType: propsedValues.efsType,
                        efsMessage: propsedValues.efsMessage,
                        objectStatus: propsedValues.objectStatus,
                        efsTemplate: propsedValues.efsTemplate ?? undefined,
                    };
                    efsObjectsCopy.push(newEfsObject);
                }
            });
            // update the state with the new efs objects
            setEfsObjects(efsObjectsCopy);
        } else {
            setEfsObjects([]);
        }
    }, [efsSelectedFeatures?.value]);

    useEffect(() => {
        if (efsObjects.length === 0) {
            setEfsManagerStatus("selection");
        }
    }, [efsObjects]);

    useEffect(() => {
        if (efsManagerStatus === "preparation") {
            setEfsMessageStringForObjects(false);
        }
    }, [efsManagerStatus]);

    useEffect(() => {
        getEfsOptions().then((efsTemplates) => {
            setEfsTemplates(efsTemplates);
        });
        getAllObjectsWithEFS().then((allEfs) => {
            setAllEfs(allEfs);
        });

        getExpiredEFS().then((expiredEfs) => {
            setExpiredEfs(expiredEfs);
        });
    }, []);

    return (
        <div className={`efs-manager-container ${efsManagerStatus !== "selection" ? "wider" : ""}`}>
            <div className="efs-manager-header w-100 flex-row space-between">
                <button
                    type="button"
                    className="empty-button"
                    style={{ position: "absolute", right: "16px", top: "16px" }}
                    onClick={closePanel}
                >
                    <CloseIcon />
                </button>
            </div>
            {checkIfDefaultVersion() && (
                <Tabs
                    className={`w-100 h-100 flex-col`}
                    disabledTabClassName="disabled"
                    selectedTabClassName="selected"
                >
                    <TabList className={`panel-content-tab-list`}>
                        <Tab className={`panel-content-tab-button`}>
                            <span className="tab-icon">Sende EFS</span>
                        </Tab>
                        <Tab className={`panel-content-tab-button`}>
                            <span className="tab-icon">Alle EFS-meldinger ({getExpiredCount()} over tiden)</span>
                        </Tab>
                    </TabList>
                    <TabPanel selectedClassName="selected" forceRender className={`panel-content-tab-panel`}>
                        <div className="efs-manager-content-container">
                            <h2>EFS Manager</h2>
                            <span>Velg ett eller flere nautiske objekter for å begynne.</span>
                            <span>Vi har prøvd å forutsi hvilke EFS-er som passer best til de valgte objektene.</span>
                            <span>Se godt over og endre der det er nødvendig.</span>
                            <div className="efs-manager-content">
                                {efsObjects.length > 0 && (
                                    <div className="efs-feature-list">
                                        <table className="efs-table">
                                            <thead>
                                                <tr>
                                                    <th style={{ width: "150px", minWidth: "150px" }} colSpan={2}>
                                                        Meldingstype
                                                    </th>
                                                    <th style={{ width: "fit-content", minWidth: "150px" }}>
                                                        Sjømerkenummer
                                                    </th>
                                                    <th style={{ width: "fit-content", minWidth: "150px" }}>Område</th>
                                                    {efsManagerStatus !== "selection" && (
                                                        <>
                                                            <th style={{ width: "50px" }}>sjøkart</th>
                                                            <th>Klasse</th>
                                                        </>
                                                    )}
                                                    <th>
                                                        <span>Fra dato</span>
                                                    </th>
                                                    <th>
                                                        <span>Til dato</span>
                                                    </th>
                                                    <th style={{ minWidth: "300px", maxWidth: "300px" }}>
                                                        EFS-melding
                                                    </th>
                                                    <th>Endringstype</th>
                                                    <th style={{ width: "50px" }}></th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {efsObjects?.map((efsObject) => (
                                                    <EFSLineItem
                                                        extended={efsManagerStatus !== "selection"}
                                                        efsObject={efsObject}
                                                        efsTemplates={efsTemplates}
                                                        setEfsObject={updateEfsObject}
                                                        key={
                                                            efsObject.nfsObject.attributes.objectid +
                                                            efsObject.nfsObject.attributes.sjomerke_nr
                                                        }
                                                    />
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                )}
                                {efsManagerStatus === "preparation" && efsObjects.length > 0 && (
                                    <div className="flex-col small-gap">
                                        <h3>Forhåndsvisning</h3>
                                        <span>
                                            Her kan du se en forhåndsvisning av EFS-meldingen som vil bli sendt.
                                        </span>
                                        <span className="small-gap">
                                            <span>Du kan laste ned en kopi av EFS-meldingen som en .txt fil.</span>
                                            <button
                                                className="nfs-button outline no-flex"
                                                style={{ width: "fit-content" }}
                                                onClick={() =>
                                                    downloadEFSObjectsAsTxt(
                                                        efsObjects,
                                                        user?.value?.fullName ?? "Ukjent avsender"
                                                    )
                                                }
                                            >
                                                Last ned kopi av EFS-melding
                                            </button>
                                        </span>
                                        <br />
                                        <span>Formatet på EFS-meldingen er som følger:</span>
                                        <span>
                                            "Utstyrsbeskrivelse, Sjøkart, Breddegrad, Lengdegrad, Klasse, Endringstype,
                                            gyldig fra, gyldig til, Meldingstekst"
                                        </span>
                                        {efsObjects.map((efsObject) => {
                                            return (
                                                <div
                                                    className="flex-row w-100 center small-gap"
                                                    key={
                                                        efsObject.nfsObject.attributes.objectid +
                                                        efsObject.nfsObject.attributes.sjomerke_nr
                                                    }
                                                >
                                                    <button
                                                        className="nfs-button outline no-flex"
                                                        style={{ height: "fit-content" }}
                                                        onClick={() => {
                                                            setEfsMessageStringForObject(efsObject, true);
                                                        }}
                                                    >
                                                        Generer på nytt
                                                    </button>
                                                    <TextArea
                                                        inputFieldId={`efs-message-preview-${efsObject.nfsObject.attributes.objectid}`}
                                                        key={`efs-message-preview-${efsObject.nfsObject.attributes.objectid}`}
                                                        value={efsObject.efsMessageToSend ?? ""}
                                                        maxLength={1000}
                                                        onChange={(newValue) => {
                                                            updateEfsObject({
                                                                ...efsObject,
                                                                efsMessageToSend: newValue,
                                                            });
                                                        }}
                                                        label={`EFS-melding for ${efsObject.nfsObject.attributes.sjomerke_nr} (${efsObject.efsType})`}
                                                    />
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}

                                <div className="efs-manager-footer">
                                    {efsManagerStatus === "preparation" && (
                                        <>
                                            <NfsButton
                                                outlined
                                                onClick={() => {
                                                    setEfsManagerStatus("selection");
                                                }}
                                                disabled={isSendingEfs}
                                            >
                                                Tilbake til valg
                                            </NfsButton>
                                            <NfsButton onClick={handleSendEfs} disabled={isSendingEfs}>
                                                {isSendingEfs ? "Sender..." : "Send EFS"}
                                            </NfsButton>
                                        </>
                                    )}
                                    {efsManagerStatus === "selection" && (
                                        <NfsButton
                                            outlined
                                            onClick={() => {
                                                setEfsManagerStatus("expanded");
                                            }}
                                            disabled={isSendingEfs || efsObjects.length === 0}
                                        >
                                            Fyll ut info (utvidet visning)
                                        </NfsButton>
                                    )}
                                    {efsManagerStatus === "expanded" && (
                                        <>
                                            <NfsButton
                                                outlined
                                                onClick={() => {
                                                    setEfsManagerStatus("selection");
                                                }}
                                                disabled={isSendingEfs}
                                            >
                                                Tilbake til valg
                                            </NfsButton>
                                            <NfsButton
                                                onClick={() => {
                                                    setEfsManagerStatus("preparation");
                                                }}
                                                disabled={isSendingEfs}
                                            >
                                                Forbered sending
                                            </NfsButton>
                                        </>
                                    )}
                                </div>
                            </div>
                        </div>
                    </TabPanel>
                    <TabPanel selectedClassName="selected" className={`panel-content-tab-panel`}>
                        <div className="efs-manager-content-container">
                            <div className="flex-row small-gap align-center">
                                <h2>Alle EFS-meldinger</h2>
                                <NfsButton className="no-flex padding-0" onClick={refreshEfsObjects}>
                                    <RefreshIcon />
                                </NfsButton>
                            </div>
                            <span>Her kan du se alle EFS-meldinger.</span>
                            <span>Huk av for meldinger du ønsker å melde som ferdig eller kansellert</span>

                            <span>Hvilke meldinger vil du se?</span>
                            <RadioButtonGroup
                                options={["Aktive", "Utløpte", "Alle"]}
                                selectedIndex={efsListMode}
                                onChange={(value) => {
                                    setEfsListMode(value as EfsListMode);
                                }}
                            />

                            <table className="efs-table">
                                <thead>
                                    <tr>
                                        <th>EFS ferdig?</th>
                                        <th>Lagnavn</th>
                                        <th>NFS status</th>
                                        <th>Meldingstype</th>
                                        <th>EFS-melding</th>
                                        <th>Gyldig fra</th>
                                        <th>Gyldig til</th>
                                        <th>Sendt av</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {Object.entries(allEfs)?.map(([layerName, efsObjects]) =>
                                        efsObjects.map((efsObject) => {
                                            if (
                                                efsListMode === EfsListMode.Active &&
                                                efsObject.attributes.gyldig_til < new Date().getTime()
                                            ) {
                                                return null; // Skip expired objects in Active mode
                                            }
                                            if (
                                                efsListMode === EfsListMode.Expired &&
                                                (efsObject.attributes.gyldig_til >= new Date().getTime() ||
                                                    efsObject.attributes.gyldig_til === null)
                                            ) {
                                                return null; // Skip active objects in Expired mode
                                            }

                                            return (
                                                <tr key={efsObject.attributes.objectid}>
                                                    <td>
                                                        <Checkbox
                                                            id={`efs-complete-${efsObject.attributes.objectid}`}
                                                            checked={isObjectMarkedForCompletion(efsObject)}
                                                            onChange={() => {
                                                                if (isObjectMarkedForCompletion(efsObject)) {
                                                                    setEfsObjectsMarkedForCompletion(
                                                                        efsObjectsMarkedForCompletion.filter(
                                                                            (obj) =>
                                                                                obj.nfsObject.attributes.objectid !==
                                                                                efsObject.attributes.objectid
                                                                        )
                                                                    );
                                                                } else {
                                                                    setEfsObjectsMarkedForCompletion([
                                                                        ...efsObjectsMarkedForCompletion,
                                                                        {
                                                                            nfsObject: efsObject,
                                                                            efsMessageToSend:
                                                                                "Denne EFS-meldingen kan avsluttes: " +
                                                                                (getCleanEfsMessage(
                                                                                    efsObject.attributes.efs_melding
                                                                                ) ?? efsObject.attributes.sjomerke_nr),
                                                                        },
                                                                    ]);
                                                                }
                                                            }}
                                                        />
                                                    </td>
                                                    <td>{layerName}</td>
                                                    <td>
                                                        {
                                                            GetCodedValueAndAlias(
                                                                efsObject.layer,
                                                                "nfs_status",
                                                                efsObject.attributes.nfs_status
                                                            )[1]
                                                        }
                                                    </td>
                                                    <td>{getEfsTypeFromMessage(efsObject.attributes.efs_melding)}</td>
                                                    <td>{getCleanEfsMessage(efsObject.attributes.efs_melding)}</td>
                                                    <td>{formatDate(efsObject.attributes.gyldig_fra)}</td>
                                                    <td>{formatDate(efsObject.attributes.gyldig_til)}</td>
                                                    <td>{efsObject.attributes.efs_sendt_av}</td>
                                                </tr>
                                            );
                                        })
                                    )}
                                </tbody>
                            </table>

                            <div>
                                <span>Følgende meldinger blir sendt</span>
                                <span className="small-gap">Du kan endre meldingen som sendes for hver EFS.</span>
                                {efsObjectsMarkedForCompletion.map((efsObject) => (
                                    <TextArea
                                        inputFieldId={`efs-completed-message-${efsObject.nfsObject.attributes.objectid}`}
                                        key={`efs-completed-message-${efsObject.nfsObject.attributes.objectid}`}
                                        value={efsObject.efsMessageToSend ?? ""}
                                        maxLength={1000}
                                        onChange={(newValue) => {
                                            handleCompletionMessageChange(efsObject.nfsObject, newValue);
                                        }}
                                        label={`EFS-melding for ${efsObject.nfsObject.attributes.sjomerke_nr}`}
                                    />
                                ))}
                                <br />
                                <NfsButton
                                    onClick={handleSendEfsMarkedForCompletion}
                                    disabled={efsObjectsMarkedForCompletion.length === 0 || isSendingEfs}
                                >
                                    Send meldinger
                                </NfsButton>
                            </div>
                        </div>
                    </TabPanel>
                </Tabs>
            )}
            {!checkIfDefaultVersion() && (
                <div className="no-default-version-warning flex-col center">
                    <span>
                        Du må være i <strong>Default (planroot)</strong> versjon for å kunne administrere EFS-meldinger.
                    </span>
                    <span>
                        Bytt til <strong>Default (planroot)</strong> versjon og prøv igjen.
                    </span>
                </div>
            )}
        </div>
    );
};

export default EFSManager;
